import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';
import { getAdminItems, createAdminItem, getFilterTable, updateAdminItem } from '../../api/recommendationAPI';
import '../Profile.css';
import { ADMIN_EMAIL } from '../../constants';

const PAGE_SIZE = 100;

const EMPTY_FORM = {
  name: '', description: '', short_description: '', price: '', sale_price: '', sale_deadline: '',
  genre: '', link: '', url: '', video: false, has_coupon: false, much_for: '',
  crowds: [], timings: [], types: [], files: [],
};

const TagSelector = ({ label, allOptions, selected, onToggle }) => {
  const selectedIds = new Set(selected.map(s => s.id));
  const available = allOptions.filter(o => !selectedIds.has(o.id));
  return (
    <div className="prog-tag-editor">
      <div className="prog-detail-label">{label}</div>
      <div className="prog-tag-list">
        {selected.map(item => (
          <span key={item.id} className="prog-tag prog-tag-removable">
            {item.name}
            <button className="prog-tag-remove" onClick={() => onToggle(item, false)}>✕</button>
          </span>
        ))}
      </div>
      {available.length > 0 && (
        <select
          className="rec-popup-input prog-tag-select"
          value=""
          onChange={e => {
            const opt = allOptions.find(o => o.id === Number(e.target.value));
            if (opt) onToggle(opt, true);
          }}
          dir="rtl"
        >
          <option value="">הוסף {label}...</option>
          {available.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
      )}
    </div>
  );
};

const GENRE_OPTIONS = ['מיניטס', 'תוכנית', 'מיתוג'];

const AddProgramPopup = ({ onClose, onAdded, allCrowds, allTimings, allTypes }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [newFileUrl, setNewFileUrl] = useState('');
  const [newFileType, setNewFileType] = useState('image');
  const [newFileForShow, setNewFileForShow] = useState(true);

  const setF = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const toggleTag = (key, item, add) =>
    setForm(f => ({
      ...f,
      [key]: add ? [...f[key], item] : f[key].filter(t => t.id !== item.id),
    }));

  const addFile = () => {
    if (!newFileUrl.trim()) return;
    setForm(f => ({ ...f, files: [...f.files, { url: newFileUrl.trim(), type: newFileType, for_show: newFileForShow }] }));
    setNewFileUrl('');
  };

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.price) { setError('שם ומחיר הם שדות חובה'); return; }
    setSaving(true);
    setError(null);
    try {
      const created = await createAdminItem({
        name: form.name,
        description: form.description,
        short_description: form.short_description,
        price: Number(form.price),
        sale_price: form.sale_price !== '' ? Number(form.sale_price) : null,
        sale_deadline: form.sale_deadline ? new Date(form.sale_deadline).toISOString() : null,
        genre: form.genre,
        link: form.link,
        url: form.url,
        video: form.video,
        has_coupon: form.has_coupon,
        much_for: form.much_for,
        crowd_ids: form.crowds.map(c => c.id),
        timing_ids: form.timings.map(t => t.id),
        type_ids: form.types.map(t => t.id),
        files: form.files,
      });
      onAdded(created);
    } catch {
      setError('שגיאה בשמירה, נסי שוב');
    } finally {
      setSaving(false);
    }
  };

  const field = (label, key, type = 'text') => (
    <div className="prog-detail-field">
      <div className="prog-detail-label">{label}</div>
      {type === 'textarea' ? (
        <textarea className="rec-popup-textarea" value={form[key]} onChange={e => setF(key, e.target.value)} rows={3} dir="rtl" />
      ) : type === 'checkbox' ? (
        <input type="checkbox" checked={!!form[key]} onChange={e => setF(key, e.target.checked)} />
      ) : (
        <input className="rec-popup-input" type={type} value={form[key] ?? ''} onChange={e => setF(key, e.target.value)} dir="rtl" />
      )}
    </div>
  );

  return (
    <div className="rec-popup-overlay" onClick={onClose}>
      <div className="add-program-popup" onClick={e => e.stopPropagation()}>
        <button className="rec-popup-close" onClick={onClose}>✕</button>
        <div className="user-details-name" style={{ marginBottom: 16 }}>תוכנית חדשה</div>

        <div className="prog-detail-grid">
          {field('שם *', 'name')}
          <div className="prog-detail-field">
            <div className="prog-detail-label">סוג</div>
            <select
              className="rec-popup-input"
              value={form.genre}
              onChange={e => setF('genre', e.target.value)}
              dir="rtl"
            >
              <option value="">בחר סוג...</option>
              {GENRE_OPTIONS.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
          {field('מחיר *', 'price', 'number')}
          {field('מחיר מבצע', 'sale_price', 'number')}
          <div className="prog-detail-field">
            <div className="prog-detail-label">דד ליין מבצע</div>
            <input
              className="rec-popup-input"
              type="datetime-local"
              value={form.sale_deadline || ''}
              onChange={e => setF('sale_deadline', e.target.value)}
            />
          </div>
          {field('לינק', 'link')}
          {field('URL', 'url')}
          {field('וידאו', 'video', 'checkbox')}
          {field('קופון', 'has_coupon', 'checkbox')}
          {field('מתאים ל', 'much_for')}
          {field('תיאור קצר', 'short_description', 'textarea')}
          {field('תיאור מלא', 'description', 'textarea')}
        </div>

        <div className="prog-detail-tags-section">
          <TagSelector label="קהלים" allOptions={allCrowds} selected={form.crowds} onToggle={(i, a) => toggleTag('crowds', i, a)} />
          <TagSelector label="תזמונים" allOptions={allTimings} selected={form.timings} onToggle={(i, a) => toggleTag('timings', i, a)} />
          <TagSelector label="סוגים" allOptions={allTypes} selected={form.types} onToggle={(i, a) => toggleTag('types', i, a)} />
        </div>

        <div className="prog-detail-files-section">
          <div className="prog-detail-label" style={{ marginBottom: 8 }}>קבצים</div>
          <div className="prog-detail-files">
            {form.files.map((f, idx) => (
              <div key={idx} className="prog-file-item prog-file-new">
                {f.type === 'image'
                  ? <img src={f.url} alt="" className="prog-detail-img" />
                  : <a href={f.url} target="_blank" rel="noreferrer" className="prog-file-link">{f.url}</a>
                }
                <button className="prog-file-remove" onClick={() => setForm(fm => ({ ...fm, files: fm.files.filter((_, i) => i !== idx) }))}>✕</button>
              </div>
            ))}
          </div>
          <div className="prog-file-add-row">
            <input className="rec-popup-input" placeholder="URL של קובץ..." value={newFileUrl} onChange={e => setNewFileUrl(e.target.value)} dir="ltr" />
            <select className="rec-popup-input prog-file-type-select" value={newFileType} onChange={e => setNewFileType(e.target.value)}>
              <option value="image">תמונה</option>
              <option value="video">וידאו</option>
              <option value="file">קובץ</option>
            </select>
            <label className="prog-file-forshow">
              <input type="checkbox" checked={newFileForShow} onChange={e => setNewFileForShow(e.target.checked)} />
              להצגה
            </label>
            <button className="prog-tag-add-btn" onClick={addFile}>+</button>
          </div>
        </div>

        {error && <div className="history-status" style={{ color: 'red' }}>{error}</div>}
        <button className="rec-popup-submit" style={{ marginTop: 16 }} onClick={handleSubmit} disabled={saving}>
          {saving ? 'שומר...' : 'הוסף תוכנית'}
        </button>
      </div>
    </div>
  );
};

const AdminPrograms = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [showAdd, setShowAdd] = useState(false);
  const [allCrowds, setAllCrowds] = useState([]);
  const [allTimings, setAllTimings] = useState([]);
  const [allTypes, setAllTypes] = useState([]);

  useEffect(() => {
    if (!user || user.email !== ADMIN_EMAIL) { navigate('/', { replace: true }); return; }
    getAdminItems()
      .then(data => setItems(Array.isArray(data) ? data : []))
      .catch(() => setError('שגיאה בטעינת תוכניות'))
      .finally(() => setLoading(false));
    Promise.all([getFilterTable('crowds'), getFilterTable('timings'), getFilterTable('types')])
      .then(([c, t, ty]) => {
        setAllCrowds(Array.isArray(c) ? c : []);
        setAllTimings(Array.isArray(t) ? t : []);
        setAllTypes(Array.isArray(ty) ? ty : []);
      });
  }, [user, navigate]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(i =>
      (i.name || '').toLowerCase().includes(q) ||
      (i.genre || '').toLowerCase().includes(q)
    );
  }, [items, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleSearch = (e) => { setSearch(e.target.value); setPage(1); };
  const handleAdded = (newItem) => { setItems(prev => [newItem, ...prev]); setShowAdd(false); };

  const handleRemoveSale = async (e, item) => {
    e.stopPropagation();
    try {
      await updateAdminItem(item.id, {
        name: item.name,
        description: item.description,
        short_description: item.short_description,
        price: Number(item.price),
        sale_price: null,
        sale_deadline: null,
        genre: item.genre,
        link: item.link,
        url: item.url,
        video: item.video,
        has_coupon: false,
        much_for: item.much_for,
        crowd_ids: (item.crowds || []).map(c => c.id),
        timing_ids: (item.timings || []).map(t => t.id),
        type_ids: (item.types || []).map(t => t.id),
        files_keep: (item.files || []).map(f => f.id),
        files_add: [],
      });
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, sale_price: null, sale_deadline: null, has_coupon: false } : i));
    } catch {
      alert('שגיאה בהסרת מבצע');
    }
  };

  if (!user || user.email !== ADMIN_EMAIL) return null;

  return (
    <div className="history-page">
      <div className="users-toolbar">
        <input
          className="users-search-input"
          placeholder="חיפוש לפי שם או סוג..."
          value={search}
          onChange={handleSearch}
          dir="rtl"
        />
        <span className="users-count">{filtered.length} תוכניות</span>
        <button className="prog-add-btn" onClick={() => setShowAdd(true)}>הוסף תוכנית</button>
      </div>

      {loading && <div className="history-status">טוען...</div>}
      {error && <div className="history-status" style={{ color: 'red' }}>{error}</div>}

      {!loading && !error && (
        <>
          <div className="users-table-wrapper">
            <table className="users-table">
              <thead>
                <tr>
                  <th>ID</th><th>שם</th><th>סוג</th><th>מחיר</th><th>מחיר מבצע</th><th>דד ליין</th><th>קונים</th><th>קופון</th><th></th>
                </tr>
              </thead>
              <tbody>
                {pageItems.length === 0 ? (
                  <tr><td colSpan="7" className="users-empty-row">לא נמצאו תוכניות</td></tr>
                ) : pageItems.map(item => (
                  <tr
                    key={item.id}
                    className="users-table-row"
                    onClick={() => navigate(`/profile/admin-programs/${item.id}`, { state: { item } })}
                  >
                    <td className="users-td-num">{item.id}</td>
                    <td>{item.name || '—'}</td>
                    <td>{item.genre || '—'}</td>
                    <td>₪{Number(item.price).toFixed(2)}</td>
                    <td>{item.sale_price != null ? `₪${Number(item.sale_price).toFixed(2)}` : '—'}</td>
                    <td>{item.sale_deadline ? new Date(item.sale_deadline).toLocaleDateString('he-IL') : '—'}</td>
                    <td className="programs-buyers-count">{item.buyers_count || 0}</td>
                    <td>{item.has_coupon ? '✓' : ''}</td>
                    <td onClick={e => e.stopPropagation()}>
                      {item.sale_price != null && (
                        <button className="prog-remove-sale-btn" onClick={e => handleRemoveSale(e, item)}>הסר מבצע</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="users-pagination">
              <button className="users-page-btn" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>›</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                <button key={p} className={`users-page-btn${currentPage === p ? ' active' : ''}`} onClick={() => setPage(p)}>{p}</button>
              ))}
              <button className="users-page-btn" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>‹</button>
            </div>
          )}
        </>
      )}

      {showAdd && (
        <AddProgramPopup
          onClose={() => setShowAdd(false)}
          onAdded={handleAdded}
          allCrowds={allCrowds}
          allTimings={allTimings}
          allTypes={allTypes}
        />
      )}
    </div>
  );
};

export default AdminPrograms;
